import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Sparkles, MessageCircle, RefreshCw } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import AstroMarkdown from "../components/AstroMarkdown";
import ChartSummaryCard from "../components/ChartSummaryCard";
import InlineBirthForm from "../components/InlineBirthForm";
import { SmartOfferBanner } from "../components/PromoBanners";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function HoroscopePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await axios.get(`${API}/horoscope/today`, { params: { lang }, withCredentials: true });
      setData(res.data);
    } catch (e) {
      setError(t("horo.failed"));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, lang]);

  const dateLabel = new Date().toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN",
    { weekday: "long", day: "numeric", month: "long" });

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "84px" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "520px", height: "520px", background: "radial-gradient(circle, rgba(255,209,102,0.10), transparent)", top: "-80px", right: "-60px", borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "420px", height: "420px", background: "radial-gradient(circle, rgba(91,30,140,0.28), transparent)", bottom: 0, left: "-80px", borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "0 16px 70px", position: "relative", zIndex: 1 }}>
        <div className="animate-fadeInUp" style={{ marginBottom: "18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Sun size={22} style={{ color: "#FFD166" }} />
            <h1 className="font-cinzel" style={{ fontSize: "clamp(1.5rem, 4vw, 2rem)", color: "#FFD166" }}>{t("horo.title")}</h1>
          </div>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.85rem", marginTop: "4px", textTransform: "capitalize" }}>{dateLabel}</p>
        </div>

        {!user ? (
          <div className="glass-card" data-testid="horo-login-gate" style={{ padding: "28px", textAlign: "center" }}>
            <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: "16px" }}>{t("horo.loginNeeded")}</p>
            <button data-testid="horo-login-btn" onClick={() => navigate("/auth?redirect=/horoscope")} className="btn-gold">{t("chat.loginToStart")}</button>
          </div>
        ) : loading ? (
          <div className="glass-card" data-testid="horo-loading" style={{ padding: "28px", display: "flex", alignItems: "center", gap: "12px" }}>
            <span className="cosmic-spinner" style={{ width: "18px", height: "18px", border: "2px solid rgba(255,209,102,0.25)", borderTop: "2px solid #FFD166" }} />
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}>{t("horo.loading")}</span>
          </div>
        ) : error ? (
          <div className="glass-card" style={{ padding: "24px" }}>
            <p data-testid="horo-error" style={{ color: "#fca5a5", marginBottom: "14px", fontSize: "0.9rem" }}>{error}</p>
            <button onClick={load} className="btn-ghost" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <RefreshCw size={14} /> {t("horo.retry")}
            </button>
          </div>
        ) : data && !data.has_chart ? (
          <div data-testid="horo-no-chart">
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem", marginBottom: "14px" }}>{t("chart.gateDesc")}</p>
            <InlineBirthForm onConnected={() => load()} defaultName={user?.name || ""} />
          </div>
        ) : data && data.has_chart ? (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: "18px" }}>
            <div className="glass-card animate-fadeInUp" data-testid="horoscope-card"
              style={{ padding: "24px", border: "1px solid rgba(255,209,102,0.2)", background: "linear-gradient(135deg, rgba(91,30,140,0.2), rgba(45,11,69,0.34))" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                <Sparkles size={15} style={{ color: "#FFD166" }} />
                <h2 data-testid="horoscope-headline" className="font-cinzel" style={{ color: "#FFD166", fontSize: "clamp(1.05rem, 3vw, 1.3rem)" }}>
                  {data.headline}
                </h2>
              </div>
              <div data-testid="horoscope-text" style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.94rem", lineHeight: 1.75 }}>
                <AstroMarkdown>{data.text}</AstroMarkdown>
              </div>
              <button data-testid="horo-ask-ai-btn"
                onClick={() => navigate(`/chat?ask=${encodeURIComponent(t("horo.askPrefill"))}`)}
                className="btn-gold" style={{ marginTop: "18px", display: "inline-flex", alignItems: "center", gap: "7px" }}>
                <MessageCircle size={15} /> {t("horo.askAi")}
              </button>
            </div>

            {data.summary && <ChartSummaryCard summary={data.summary} name={data.name} />}
            <SmartOfferBanner />
          </div>
        ) : null}
      </div>
    </div>
  );
}
